/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, CalendarDays, Clock, Check } from "lucide-react";
import { api, SERVICES, TIME_SLOTS } from "@/lib/divine";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  phone: z.string().min(8, "Enter a valid phone"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  service: z.string().min(1, "Choose a service"),
  time_slot: z.string().min(1, "Choose a time"),
  message: z.string().optional(),
});

export default function Booking() {
  const [date, setDate] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", phone: "", email: "", service: "", time_slot: "", message: "" },
  });

  const onSubmit = async (values) => {
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    setLoading(true);
    try {
      // Format as local YYYY-MM-DD to avoid IST->UTC day-shift (toISOString bug)
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const payload = { ...values, date: `${y}-${m}-${d}` };
      await api.post("/bookings", payload);
      setSubmitted(true);
      toast.success("Booking received! We'll be in touch shortly.");
    } catch (e) {
      toast.error("Could not submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="booking-success" className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="text-center max-w-xl">
          <div className="w-16 h-16 mx-auto border border-[#D4A437] flex items-center justify-center mb-8">
            <Check className="w-8 h-8 text-[#D4A437]" strokeWidth={1.3} />
          </div>
          <div className="overline mb-4">Confirmed</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Thank you for reaching out.
          </h1>
          <p className="text-[#6B6B6B] mt-6 leading-relaxed">
            Your consultation request has been received. We'll call you on the number you shared within 24 hours to confirm your slot.
          </p>
          <a href="/" className="btn-ghost mt-10 inline-flex">Return Home</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen">
      <section className="pt-16 pb-12 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-6">Book a Consultation</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Begin your <span className="italic text-[#D4A437]">journey</span>.
          </h1>
          <p className="text-[#6B6B6B] mt-6 max-w-xl">
            Pick a date and time. Share a few details. We'll take it from there.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit(onSubmit)} className="grid lg:grid-cols-12 gap-10">
            {/* Calendar side */}
            <div className="lg:col-span-5">
              <div className="border border-black/10 bg-[#FAF7F0] p-6">
                <div className="flex items-center gap-3 mb-6">
                  <CalendarDays className="w-5 h-5 text-[#D4A437]" strokeWidth={1.5} />
                  <span className="overline">Select Date</span>
                </div>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  data-testid="booking-calendar"
                  className="mx-auto"
                />
              </div>

              <div className="border border-black/10 bg-[#FAF7F0] p-6 mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-[#D4A437]" strokeWidth={1.5} />
                  <span className="overline">Select Time</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setValue("time_slot", t, { shouldValidate: true })}
                      data-testid={`slot-${t.replace(/[: ]/g, '')}`}
                      className={`text-xs tracking-wider py-3 px-2 border transition-all ${
                        watch("time_slot") === t
                          ? "bg-[#D4A437] text-[#0A0A0A] border-[#D4A437]"
                          : "border-black/15 text-[#0A0A0A]/75 hover:border-[#D4A437] hover:text-[#D4A437]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {errors.time_slot && <p className="text-red-400 text-xs mt-3">{errors.time_slot.message}</p>}
              </div>
            </div>

            {/* Form side */}
            <div className="lg:col-span-7 space-y-6">
              <Field label="Your Name" error={errors.name?.message}>
                <input
                  {...register("name")}
                  data-testid="booking-name"
                  type="text"
                  placeholder="e.g. Priya Sharma"
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#D4A437] outline-none py-3 text-[#0A0A0A] placeholder-[#9A9A9A] text-lg"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Phone" error={errors.phone?.message}>
                  <input
                    {...register("phone")}
                    data-testid="booking-phone"
                    type="tel"
                    placeholder="+91 99XXX XXXXX"
                    className="w-full bg-transparent border-b border-black/15 focus:border-[#D4A437] outline-none py-3 text-[#0A0A0A] placeholder-[#9A9A9A] text-lg"
                  />
                </Field>
                <Field label="Email (Optional)" error={errors.email?.message}>
                  <input
                    {...register("email")}
                    data-testid="booking-email"
                    type="email"
                    placeholder="you@example.com"
                    className="w-full bg-transparent border-b border-black/15 focus:border-[#D4A437] outline-none py-3 text-[#0A0A0A] placeholder-[#9A9A9A] text-lg"
                  />
                </Field>
              </div>

              <Field label="Service" error={errors.service?.message}>
                <Select onValueChange={(v) => setValue("service", v, { shouldValidate: true })}>
                  <SelectTrigger data-testid="booking-service" className="w-full bg-transparent border-0 border-b border-black/15 rounded-none py-6 text-lg focus:ring-0 focus:border-[#D4A437] text-[#0A0A0A]">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F5F0E5] border-black/10 text-[#0A0A0A]">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.key} value={s.title} data-testid={`service-opt-${s.key.toLowerCase().replace(/\s+/g, '-')}`}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Tell us about your space (Optional)">
                <textarea
                  {...register("message")}
                  data-testid="booking-message"
                  rows={4}
                  placeholder="Type of property, size, what you're hoping to achieve..."
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#D4A437] outline-none py-3 text-[#0A0A0A] placeholder-[#9A9A9A] resize-none"
                />
              </Field>

              {/* Summary */}
              <div className="border border-black/10 p-6 bg-[#FAF7F0] mt-8">
                <div className="overline mb-4">Booking Summary</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-[#9A9A9A] text-xs uppercase tracking-wider">Date</div><div className="text-[#0A0A0A] mt-1">{date ? date.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—"}</div></div>
                  <div><div className="text-[#9A9A9A] text-xs uppercase tracking-wider">Time</div><div className="text-[#0A0A0A] mt-1">{watch("time_slot") || "—"}</div></div>
                  <div className="col-span-2"><div className="text-[#9A9A9A] text-xs uppercase tracking-wider">Service</div><div className="text-[#0A0A0A] mt-1">{watch("service") || "—"}</div></div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="btn-gold w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Submitting..." : "Confirm Booking"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
    </div>
  );
}
